import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LogOut, Inbox, MessageSquare, BookOpen, Users, Newspaper, BarChart3, Check, Trash2, Eye, Plus, Pencil, X, BookHeart, QrCode, LineChart, Mail, Handshake, ShoppingCart, Tablet } from "lucide-react";
import { api, GENRES, RECO_GENRES, genreLabel, recoGenreLabel } from "../lib/api";
import { ExtractsPanel } from "../components/admin/ExtractsPanel";
import { ExtractStatsPanel } from "../components/admin/ExtractStatsPanel";
import { NewsletterPanel } from "../components/admin/NewsletterPanel";
import { PartnersPanel } from "../components/admin/PartnersPanel";
import { OrdersPanel } from "../components/admin/OrdersPanel";
import { EbookSalesPanel } from "../components/admin/EbookSalesPanel";
import { useAuth } from "../lib/auth";

const TABS = [
  { id: "stats", label: "Statistiques", icon: BarChart3 },
  { id: "submissions", label: "Soumissions", icon: Inbox },
  { id: "contacts", label: "Messages", icon: MessageSquare },
  { id: "books", label: "Livres", icon: BookOpen },
  { id: "authors", label: "Auteurs", icon: Users },
  { id: "news", label: "Actualités", icon: Newspaper },
  { id: "recommendations", label: "Lectures recommandées", icon: BookHeart },
  { id: "extracts", label: "Extraits QR", icon: QrCode },
  { id: "partners", label: "Partenaires", icon: Handshake },
  { id: "orders", label: "Commandes", icon: ShoppingCart },
  { id: "ebook-sales", label: "Ventes numériques", icon: Tablet },
  { id: "extract-stats", label: "Stats histoires", icon: LineChart },
  { id: "newsletter", label: "Newsletter", icon: Mail },
];

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("stats");

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><p className="font-body text-[#757575]">Chargement…</p></div>;
  if (!user || !user.is_admin) return <Navigate to="/admin/login" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div data-testid="page-admin" className="min-h-screen bg-[#FAF9F6]">
      {/* Admin header */}
      <header className="border-b border-[#D1CFC7] bg-[#FAF9F6] sticky top-0 z-40">
        <div className="px-6 md:px-12 py-5 flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#9E4735] font-body">Tableau de bord</p>
            <h1 className="font-serif text-2xl text-[#1A1A1A]">CLED Admin</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-body text-[#1A1A1A]">{user.name}</p>
              <p className="text-xs font-body text-[#757575]">{user.email}</p>
            </div>
            <button onClick={handleLogout} data-testid="logout-button" className="border border-[#D1CFC7] hover:border-[#2C402E] text-[#1A1A1A] px-4 py-2 text-sm font-body flex items-center gap-2 transition-colors">
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
        {/* Tabs */}
        <nav className="px-6 md:px-12 flex gap-1 overflow-x-auto border-t border-[#D1CFC7]/50">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-body whitespace-nowrap transition-colors border-b-2 ${
                tab === t.id ? "border-[#2C402E] text-[#2C402E]" : "border-transparent text-[#4A4A4A] hover:text-[#1A1A1A]"
              }`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="px-6 md:px-12 py-10">
        {tab === "stats" && <StatsPanel />}
        {tab === "submissions" && <SubmissionsPanel />}
        {tab === "contacts" && <ContactsPanel />}
        {tab === "books" && <BooksPanel />}
        {tab === "authors" && <AuthorsPanel />}
        {tab === "news" && <NewsPanel />}
        {tab === "recommendations" && <RecommendationsPanel />}
        {tab === "extracts" && <ExtractsPanel />}
        {tab === "partners" && <PartnersPanel />}
        {tab === "orders" && <OrdersPanel />}
        {tab === "ebook-sales" && <EbookSalesPanel />}
        {tab === "extract-stats" && <ExtractStatsPanel />}
        {tab === "newsletter" && <NewsletterPanel />}
      </main>
    </div>
  );
}

// ============ STATS ============
function StatsPanel() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats", { withCredentials: true }).then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);
  if (!stats) return <p className="font-body text-[#757575]" data-testid="stats-loading">Chargement…</p>;
  const cards = [
    { label: "Livres", value: stats.books },
    { label: "Auteurs", value: stats.authors },
    { label: "Actualités", value: stats.news },
    { label: "Soumissions totales", value: stats.submissions_total },
    { label: "Soumissions en attente", value: stats.submissions_pending, highlight: true },
    { label: "Messages totaux", value: stats.contacts_total },
    { label: "Nouveaux messages", value: stats.contacts_new, highlight: true },
    { label: "Visites trackées", value: stats.visits },
    { label: "Extraits QR", value: stats.extracts ?? 0 },
  ];
  return (
    <div data-testid="stats-panel">
      <h2 className="font-serif text-4xl text-[#1A1A1A] font-light mb-8">Vue d'ensemble</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {cards.map((c) => (
          <div key={c.label} data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`} className={`border p-6 ${c.highlight && c.value > 0 ? "border-[#9E4735] bg-[#FAF9F6]" : "border-[#D1CFC7]"}`}>
            <p className="text-xs tracking-[0.2em] uppercase text-[#757575] font-body mb-3">{c.label}</p>
            <p className="font-serif text-4xl text-[#2C402E]">{c.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ SUBMISSIONS ============
function SubmissionsPanel() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/admin/submissions", { withCredentials: true }).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/submissions/${id}`, { status }, { withCredentials: true });
    load();
    if (selected?.id === id) setSelected({ ...selected, status });
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette soumission ?")) return;
    await api.delete(`/admin/submissions/${id}`, { withCredentials: true });
    load();
    if (selected?.id === id) setSelected(null);
  };

  return (
    <div data-testid="submissions-panel">
      <h2 className="font-serif text-4xl text-[#1A1A1A] font-light mb-8">Soumissions de manuscrits ({items.length})</h2>
      {items.length === 0 ? (
        <p className="font-body text-[#757575]">Aucune soumission pour le moment.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <ul className="divide-y divide-[#D1CFC7]">
            {items.map((s) => (
              <li key={s.id} data-testid={`submission-${s.id}`} onClick={() => setSelected(s)} className={`py-4 cursor-pointer hover:bg-[#F4F1EA] px-3 -mx-3 transition-colors ${selected?.id === s.id ? "bg-[#F4F1EA]" : ""}`}>
                <div className="flex items-center justify-between gap-4 mb-1">
                  <p className="font-serif text-lg text-[#1A1A1A] truncate">{s.title}</p>
                  <span className={`text-[10px] uppercase tracking-[0.2em] font-body px-2 py-0.5 ${s.status === "pending" ? "bg-[#9E4735] text-[#FAF9F6]" : s.status === "accepted" ? "bg-[#2C402E] text-[#FAF9F6]" : "bg-[#757575] text-[#FAF9F6]"}`}>{s.status}</span>
                </div>
                <p className="text-sm font-body text-[#4A4A4A]">{s.full_name} · {genreLabel(s.genre)}</p>
                <p className="text-xs font-body text-[#757575] mt-1">{new Date(s.created_at).toLocaleString("fr-FR")}</p>
              </li>
            ))}
          </ul>
          {selected && (
            <div data-testid="submission-detail" className="border border-[#D1CFC7] p-6 sticky top-32 self-start max-h-[calc(100vh-10rem)] overflow-y-auto">
              <div className="flex justify-between items-start mb-4">
                <h3 className="font-serif text-2xl text-[#1A1A1A]">{selected.title}</h3>
                <button onClick={() => setSelected(null)} data-testid="close-detail"><X size={18} /></button>
              </div>
              <dl className="grid grid-cols-2 gap-3 text-sm font-body mb-6">
                <dt className="text-[#757575]">Auteur</dt><dd className="text-[#1A1A1A]">{selected.full_name}</dd>
                <dt className="text-[#757575]">Email</dt><dd className="text-[#1A1A1A] break-all">{selected.email}</dd>
                <dt className="text-[#757575]">Téléphone</dt><dd className="text-[#1A1A1A]">{selected.phone || "—"}</dd>
                <dt className="text-[#757575]">Genre</dt><dd className="text-[#1A1A1A]">{genreLabel(selected.genre)}</dd>
                <dt className="text-[#757575]">Pages</dt><dd className="text-[#1A1A1A]">{selected.pages_count || "—"}</dd>
              </dl>
              <Block title="Synopsis">{selected.synopsis}</Block>
              <Block title="Biographie">{selected.biography}</Block>
              {selected.manuscript_excerpt && <Block title="Extrait">{selected.manuscript_excerpt}</Block>}
              <div className="flex flex-wrap gap-2 mt-6">
                <button onClick={() => updateStatus(selected.id, "accepted")} data-testid="accept-submission" className="bg-[#2C402E] text-[#FAF9F6] px-4 py-2 text-sm font-body flex items-center gap-2"><Check size={14} /> Accepter</button>
                <button onClick={() => updateStatus(selected.id, "rejected")} data-testid="reject-submission" className="border border-[#9E4735] text-[#9E4735] px-4 py-2 text-sm font-body">Refuser</button>
                <button onClick={() => updateStatus(selected.id, "pending")} className="border border-[#D1CFC7] text-[#4A4A4A] px-4 py-2 text-sm font-body">En attente</button>
                <button onClick={() => remove(selected.id)} data-testid="delete-submission" className="ml-auto border border-[#D1CFC7] text-[#9E4735] px-4 py-2 text-sm font-body flex items-center gap-2"><Trash2 size={14} /> Supprimer</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============ CONTACTS ============
function ContactsPanel() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/contacts", { withCredentials: true }).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const markRead = async (id) => { await api.patch(`/admin/contacts/${id}`, { status: "read" }, { withCredentials: true }); load(); };
  const remove = async (id) => { if (!window.confirm("Supprimer ce message ?")) return; await api.delete(`/admin/contacts/${id}`, { withCredentials: true }); load(); };

  return (
    <div data-testid="contacts-panel">
      <h2 className="font-serif text-4xl text-[#1A1A1A] font-light mb-8">Messages de contact ({items.length})</h2>
      {items.length === 0 ? (
        <p className="font-body text-[#757575]">Aucun message pour le moment.</p>
      ) : (
        <ul className="divide-y divide-[#D1CFC7]">
          {items.map((c) => (
            <li key={c.id} data-testid={`contact-${c.id}`} className="py-6">
              <div className="flex items-start justify-between gap-4 mb-2">
                <div>
                  <p className="font-serif text-xl text-[#1A1A1A]">{c.subject}</p>
                  <p className="text-sm font-body text-[#4A4A4A]">{c.full_name} · <a href={`mailto:${c.email}`} className="link-underline">{c.email}</a></p>
                </div>
                <span className={`text-[10px] uppercase tracking-[0.2em] font-body px-2 py-0.5 ${c.status === "new" ? "bg-[#9E4735] text-[#FAF9F6]" : "bg-[#757575] text-[#FAF9F6]"}`}>{c.status}</span>
              </div>
              <p className="text-sm font-body text-[#1A1A1A] whitespace-pre-wrap mt-2 mb-3">{c.message}</p>
              <p className="text-xs font-body text-[#757575] mb-3">{new Date(c.created_at).toLocaleString("fr-FR")}</p>
              <div className="flex gap-2">
                <a href={`mailto:${c.email}?subject=Re: ${encodeURIComponent(c.subject)}`} data-testid={`reply-${c.id}`} className="bg-[#2C402E] text-[#FAF9F6] px-4 py-2 text-sm font-body">Répondre par email</a>
                {c.status === "new" && <button onClick={() => markRead(c.id)} data-testid={`mark-read-${c.id}`} className="border border-[#D1CFC7] text-[#4A4A4A] px-4 py-2 text-sm font-body flex items-center gap-2"><Eye size={14} /> Marquer comme lu</button>}
                <button onClick={() => remove(c.id)} data-testid={`delete-contact-${c.id}`} className="ml-auto border border-[#D1CFC7] text-[#9E4735] px-4 py-2 text-sm font-body flex items-center gap-2"><Trash2 size={14} /> Supprimer</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ============ BOOKS CRUD ============
const EMPTY_BOOK = { title: "", author: "", genre: "roman", description: "", cover_url: "", price: "", pages: "", isbn: "", publication_date: "", featured: false, is_new: false, amazon_url: "" };
function BooksPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/books").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...editing, price: editing.price ? parseFloat(editing.price) : null, pages: editing.pages ? parseInt(editing.pages, 10) : null };
    if (editing.id) await api.put(`/admin/books/${editing.id}`, payload, { withCredentials: true });
    else await api.post("/admin/books", payload, { withCredentials: true });
    setEditing(null); load();
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ce livre ?")) return; await api.delete(`/admin/books/${id}`, { withCredentials: true }); load(); };

  return (
    <div data-testid="books-panel">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-serif text-4xl text-[#1A1A1A] font-light">Catalogue ({items.length})</h2>
        <button onClick={() => setEditing({ ...EMPTY_BOOK })} data-testid="new-book-button" className="bg-[#2C402E] text-[#FAF9F6] px-4 py-2 text-sm font-body flex items-center gap-2"><Plus size={14} /> Nouveau livre</button>
      </div>
      {editing && <BookForm value={editing} onChange={setEditing} onSave={save} onCancel={() => setEditing(null)} />}
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
        {items.map((b) => (
          <li key={b.id} data-testid={`admin-book-${b.id}`} className="flex gap-4 border border-[#D1CFC7] p-4">
            <img src={b.cover_url} alt={b.title} className="w-16 h-24 object-cover bg-[#F4F1EA] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-serif text-lg text-[#1A1A1A] truncate">{b.title}</p>
              <p className="text-sm font-body text-[#4A4A4A]">{b.author}</p>
              <p className="text-xs font-body text-[#757575] mt-1">{genreLabel(b.genre)} · {b.price ? `${b.price.toFixed(2)} €` : "—"}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEditing({ ...b, price: b.price ?? "", pages: b.pages ?? "" })} data-testid={`edit-book-${b.id}`} className="text-xs font-body text-[#2C402E] flex items-center gap-1"><Pencil size={12} /> Éditer</button>
                <button onClick={() => remove(b.id)} data-testid={`delete-book-${b.id}`} className="text-xs font-body text-[#9E4735] flex items-center gap-1"><Trash2 size={12} /> Supprimer</button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BookForm({ value, onChange, onSave, onCancel }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div data-testid="book-form" className="border border-[#2C402E] p-6 bg-[#F4F1EA]">
      <h3 className="font-serif text-2xl text-[#1A1A1A] mb-6">{value.id ? "Modifier le livre" : "Nouveau livre"}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input label="Titre *" value={value.title} onChange={(e) => set("title", e.target.value)} testid="book-title" />
        <Input label="Auteur *" value={value.author} onChange={(e) => set("author", e.target.value)} testid="book-author" />
        <div>
          <label className="text-xs font-body text-[#4A4A4A] mb-1 block">Genre *</label>
          <select value={value.genre} onChange={(e) => set("genre", e.target.value)} data-testid="book-genre" className="w-full bg-[#FAF9F6] border border-[#D1CFC7] focus:border-[#2C402E] px-3 py-2 outline-none font-body text-sm">
            {GENRES.filter((g) => g.value !== "all").map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
        </div>
        <Input label="Prix (€)" type="number" step="0.01" value={value.price} onChange={(e) => set("price", e.target.value)} testid="book-price" />
        <Input label="Pages" type="number" value={value.pages} onChange={(e) => set("pages", e.target.value)} testid="book-pages" />
        <Input label="ISBN" value={value.isbn || ""} onChange={(e) => set("isbn", e.target.value)} testid="book-isbn" />
        <Input label="Date de publication *" type="date" value={value.publication_date} onChange={(e) => set("publication_date", e.target.value)} testid="book-date" />
        <Input label="URL couverture *" value={value.cover_url} onChange={(e) => set("cover_url", e.target.value)} testid="book-cover" />
        <Input label="URL Amazon/Fnac" value={value.amazon_url || ""} onChange={(e) => set("amazon_url", e.target.value)} testid="book-amazon" />
        <div className="flex items-center gap-6 pt-6">
          <label className="flex items-center gap-2 text-sm font-body"><input type="checkbox" checked={value.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="book-featured" /> En vedette</label>
          <label className="flex items-center gap-2 text-sm font-body"><input type="checkbox" checked={value.is_new} onChange={(e) => set("is_new", e.target.checked)} data-testid="book-isnew" /> Nouveauté</label>
        </div>
      </div>
      <div className="md:col-span-2 mt-4">
        <label className="text-xs font-body text-[#4A4A4A] mb-1 block">Description *</label>
        <textarea value={value.description} onChange={(e) => set("description", e.target.value)} rows={4} data-testid="book-description" className="w-full bg-[#FAF9F6] border border-[#D1CFC7] focus:border-[#2C402E] px-3 py-2 outline-none font-body text-sm" />
      </div>
      <div className="flex gap-2 mt-6">
        <button onClick={onSave} data-testid="save-book" className="bg-[#2C402E] text-[#FAF9F6] px-6 py-2 text-sm font-body">Enregistrer</button>
        <button onClick={onCancel} data-testid="cancel-book" className="border border-[#D1CFC7] px-6 py-2 text-sm font-body">Annuler</button>
      </div>
    </div>
  );
}

// ============ AUTHORS CRUD ============
const EMPTY_AUTHOR = { name: "", bio: "", photo_url: "", books_count: 0, latest_book: "", genres: [] };
function AuthorsPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/authors").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const save = async () => {
    const payload = { ...editing, books_count: parseInt(editing.books_count || 0, 10), genres: typeof editing.genres === "string" ? editing.genres.split(",").map(s => s.trim()).filter(Boolean) : editing.genres };
    if (editing.id) await api.put(`/admin/authors/${editing.id}`, payload, { withCredentials: true });
    else await api.post("/admin/authors", payload, { withCredentials: true });
    setEditing(null); load();
  };
  const remove = async (id) => { if (!window.confirm("Supprimer cet auteur ?")) return; await api.delete(`/admin/authors/${id}`, { withCredentials: true }); load(); };

  return (
    <div data-testid="authors-panel">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-serif text-4xl text-[#1A1A1A] font-light">Auteurs ({items.length})</h2>
        <button onClick={() => setEditing({ ...EMPTY_AUTHOR })} data-testid="new-author-button" className="bg-[#2C402E] text-[#FAF9F6] px-4 py-2 text-sm font-body flex items-center gap-2"><Plus size={14} /> Nouvel auteur</button>
      </div>
      {editing && (
        <div className="border border-[#2C402E] p-6 bg-[#F4F1EA]">
          <h3 className="font-serif text-2xl text-[#1A1A1A] mb-6">{editing.id ? "Modifier l'auteur" : "Nouvel auteur"}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Nom *" value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})} testid="author-name" />
            <Input label="URL photo *" value={editing.photo_url} onChange={(e) => setEditing({...editing, photo_url: e.target.value})} testid="author-photo" />
            <Input label="Nombre de livres" type="number" value={editing.books_count} onChange={(e) => setEditing({...editing, books_count: e.target.value})} testid="author-count" />
            <Input label="Dernier livre" value={editing.latest_book || ""} onChange={(e) => setEditing({...editing, latest_book: e.target.value})} testid="author-latest" />
            <Input label="Genres (séparés par virgule)" value={Array.isArray(editing.genres) ? editing.genres.join(", ") : editing.genres} onChange={(e) => setEditing({...editing, genres: e.target.value})} testid="author-genres" />
          </div>
          <div className="mt-4">
            <label className="text-xs font-body text-[#4A4A4A] mb-1 block">Biographie *</label>
            <textarea value={editing.bio} onChange={(e) => setEditing({...editing, bio: e.target.value})} rows={4} data-testid="author-bio" className="w-full bg-[#FAF9F6] border border-[#D1CFC7] focus:border-[#2C402E] px-3 py-2 outline-none font-body text-sm" />
          </div>
          <div className="flex gap-2 mt-6">
            <button onClick={save} data-testid="save-author" className="bg-[#2C402E] text-[#FAF9F6] px-6 py-2 text-sm font-body">Enregistrer</button>
            <button onClick={() => setEditing(null)} data-testid="cancel-author" className="border border-[#D1CFC7] px-6 py-2 text-sm font-body">Annuler</button>
          </div>
        </div>
      )}
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
        {items.map((a) => (
          <li key={a.id} data-testid={`admin-author-${a.id}`} className="flex gap-4 border border-[#D1CFC7] p-4">
            <img src={a.photo_url} alt={a.name} className="w-16 h-16 object-cover bg-[#F4F1EA] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-serif text-lg text-[#1A1A1A]">{a.name}</p>
              <p className="text-xs font-body text-[#757575]">{a.books_count} livre{a.books_count > 1 ? "s" : ""}</p>
              <p className="text-xs font-body text-[#4A4A4A] mt-1 line-clamp-2">{a.bio}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEditing(a)} data-testid={`edit-author-${a.id}`} className="text-xs font-body text-[#2C402E] flex items-center gap-1"><Pencil size={12} /> Éditer</button>
                <button onClick={() => remove(a.id)} data-testid={`delete-author-${a.id}`} className="text-xs font-body text-[#9E4735] flex items-center gap-1"><Trash2 size={12} /> Supprimer</button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============ NEWS CRUD ============
const EMPTY_NEWS = { title: "", excerpt: "", date: "", category: "", image_url: "" };
function NewsPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/news").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (editing.id) await api.put(`/admin/news/${editing.id}`, editing, { withCredentials: true });
    else await api.post("/admin/news", editing, { withCredentials: true });
    setEditing(null); load();
  };
  const remove = async (id) => { if (!window.confirm("Supprimer cette actualité ?")) return; await api.delete(`/admin/news/${id}`, { withCredentials: true }); load(); };

  return (
    <div data-testid="news-panel">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-serif text-4xl text-[#1A1A1A] font-light">Actualités ({items.length})</h2>
        <button onClick={() => setEditing({ ...EMPTY_NEWS })} data-testid="new-news-button" className="bg-[#2C402E] text-[#FAF9F6] px-4 py-2 text-sm font-body flex items-center gap-2"><Plus size={14} /> Nouvelle actualité</button>
      </div>
      {editing && (
        <div className="border border-[#2C402E] p-6 bg-[#F4F1EA]">
          <h3 className="font-serif text-2xl text-[#1A1A1A] mb-6">{editing.id ? "Modifier l'actualité" : "Nouvelle actualité"}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Titre *" value={editing.title} onChange={(e) => setEditing({...editing, title: e.target.value})} testid="news-title" />
            <Input label="Catégorie *" value={editing.category} onChange={(e) => setEditing({...editing, category: e.target.value})} testid="news-category" />
            <Input label="Date *" type="date" value={editing.date} onChange={(e) => setEditing({...editing, date: e.target.value})} testid="news-date" />
            <Input label="URL image" value={editing.image_url || ""} onChange={(e) => setEditing({...editing, image_url: e.target.value})} testid="news-image" />
          </div>
          <div className="mt-4">
            <label className="text-xs font-body text-[#4A4A4A] mb-1 block">Extrait *</label>
            <textarea value={editing.excerpt} onChange={(e) => setEditing({...editing, excerpt: e.target.value})} rows={4} data-testid="news-excerpt" className="w-full bg-[#FAF9F6] border border-[#D1CFC7] focus:border-[#2C402E] px-3 py-2 outline-none font-body text-sm" />
          </div>
          <div className="flex gap-2 mt-6">
            <button onClick={save} data-testid="save-news" className="bg-[#2C402E] text-[#FAF9F6] px-6 py-2 text-sm font-body">Enregistrer</button>
            <button onClick={() => setEditing(null)} data-testid="cancel-news" className="border border-[#D1CFC7] px-6 py-2 text-sm font-body">Annuler</button>
          </div>
        </div>
      )}
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
        {items.map((n) => (
          <li key={n.id} data-testid={`admin-news-${n.id}`} className="border border-[#D1CFC7] p-4">
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#9E4735] font-body mb-2">{n.category} · {new Date(n.date).toLocaleDateString("fr-FR")}</p>
            <p className="font-serif text-lg text-[#1A1A1A] mb-2">{n.title}</p>
            <p className="text-xs font-body text-[#4A4A4A] line-clamp-3">{n.excerpt}</p>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setEditing(n)} data-testid={`edit-news-${n.id}`} className="text-xs font-body text-[#2C402E] flex items-center gap-1"><Pencil size={12} /> Éditer</button>
              <button onClick={() => remove(n.id)} data-testid={`delete-news-${n.id}`} className="text-xs font-body text-[#9E4735] flex items-center gap-1"><Trash2 size={12} /> Supprimer</button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============ RECOMMENDATIONS CRUD (7switch affiliate) ============
const EMPTY_RECO = { title: "", author: "", cover_url: "", description: "", price: "", genre: "roman", affiliate_url: "" };
function RecommendationsPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/recommendations", { withCredentials: true }).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...editing, price: editing.price ? parseFloat(editing.price) : null };
    if (editing.id) await api.put(`/admin/recommendations/${editing.id}`, payload, { withCredentials: true });
    else await api.post("/admin/recommendations", payload, { withCredentials: true });
    setEditing(null); load();
  };
  const remove = async (id) => { if (!window.confirm("Supprimer cette recommandation ?")) return; await api.delete(`/admin/recommendations/${id}`, { withCredentials: true }); load(); };

  return (
    <div data-testid="recommendations-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-4xl text-[#1A1A1A] font-light">Lectures recommandées ({items.length})</h2>
        <button onClick={() => setEditing({ ...EMPTY_RECO })} data-testid="new-reco-button" className="bg-[#2C402E] text-[#FAF9F6] px-4 py-2 text-sm font-body flex items-center gap-2"><Plus size={14} /> Nouvelle recommandation</button>
      </div>
      <p className="text-sm font-body text-[#757575] mb-8 max-w-3xl">
        Ajoutez ici les livres vendus par 7switch que vous souhaitez recommander. Chaque achat via votre lien d'affiliation génère une commission pour CLED.
      </p>
      {editing && <RecommendationForm value={editing} onChange={setEditing} onSave={save} onCancel={() => setEditing(null)} />}
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
        {items.map((r) => (
          <li key={r.id} data-testid={`admin-reco-${r.id}`} className="flex gap-4 border border-[#D1CFC7] p-4">
            <img src={r.cover_url} alt={r.title} className="w-16 h-24 object-cover bg-[#F4F1EA] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-serif text-lg text-[#1A1A1A] truncate">{r.title}</p>
              <p className="text-sm font-body text-[#4A4A4A]">{r.author}</p>
              <p className="text-xs font-body text-[#757575] mt-1">{recoGenreLabel(r.genre)} · {r.price ? `${r.price.toFixed(2)} €` : "—"}</p>
              <a href={r.affiliate_url} target="_blank" rel="noreferrer" className="text-xs font-body text-[#C75D2C] underline mt-1 block truncate" title={r.affiliate_url}>Lien 7switch ↗</a>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEditing({ ...r, price: r.price ?? "" })} data-testid={`edit-reco-${r.id}`} className="text-xs font-body text-[#2C402E] flex items-center gap-1"><Pencil size={12} /> Éditer</button>
                <button onClick={() => remove(r.id)} data-testid={`delete-reco-${r.id}`} className="text-xs font-body text-[#9E4735] flex items-center gap-1"><Trash2 size={12} /> Supprimer</button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RecommendationForm({ value, onChange, onSave, onCancel }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div data-testid="reco-form" className="border border-[#2C402E] p-6 bg-[#F4F1EA]">
      <h3 className="font-serif text-2xl text-[#1A1A1A] mb-6">{value.id ? "Modifier la recommandation" : "Nouvelle recommandation 7switch"}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input label="Titre *" value={value.title} onChange={(e) => set("title", e.target.value)} testid="reco-title" />
        <Input label="Auteur *" value={value.author} onChange={(e) => set("author", e.target.value)} testid="reco-author" />
        <div>
          <label className="text-xs font-body text-[#4A4A4A] mb-1 block">Genre *</label>
          <select value={value.genre} onChange={(e) => set("genre", e.target.value)} data-testid="reco-genre" className="w-full bg-[#FAF9F6] border border-[#D1CFC7] focus:border-[#2C402E] px-3 py-2 outline-none font-body text-sm">
            {RECO_GENRES.filter((g) => g.value !== "all").map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
        </div>
        <Input label="Prix (€)" type="number" step="0.01" value={value.price} onChange={(e) => set("price", e.target.value)} testid="reco-price" />
        <Input label="URL couverture *" value={value.cover_url} onChange={(e) => set("cover_url", e.target.value)} testid="reco-cover" />
        <Input label="Lien d'affiliation 7switch *" value={value.affiliate_url} onChange={(e) => set("affiliate_url", e.target.value)} testid="reco-affiliate" />
      </div>
      <div className="mt-4">
        <label className="text-xs font-body text-[#4A4A4A] mb-1 block">Description *</label>
        <textarea value={value.description} onChange={(e) => set("description", e.target.value)} rows={4} data-testid="reco-description" className="w-full bg-[#FAF9F6] border border-[#D1CFC7] focus:border-[#2C402E] px-3 py-2 outline-none font-body text-sm" />
      </div>
      <div className="flex gap-2 mt-6">
        <button onClick={onSave} data-testid="save-reco" className="bg-[#2C402E] text-[#FAF9F6] px-6 py-2 text-sm font-body">Enregistrer</button>
        <button onClick={onCancel} data-testid="cancel-reco" className="border border-[#D1CFC7] px-6 py-2 text-sm font-body">Annuler</button>
      </div>
    </div>
  );
}

// ============ Helpers ============
const Input = ({ label, testid, ...props }) => (
  <div>
    <label className="text-xs font-body text-[#4A4A4A] mb-1 block">{label}</label>
    <input {...props} data-testid={testid} className="w-full bg-[#FAF9F6] border border-[#D1CFC7] focus:border-[#2C402E] px-3 py-2 outline-none font-body text-sm" />
  </div>
);

const Block = ({ title, children }) => (
  <div className="mb-4">
    <p className="text-xs uppercase tracking-[0.2em] text-[#9E4735] font-body mb-2">{title}</p>
    <p className="text-sm font-body text-[#1A1A1A] whitespace-pre-wrap">{children}</p>
  </div>
);
